import { ApiService } from '../utils/ApiService';
import { chevronDownIcon } from './icons';

/** Element.remove() is missing from the desktop ES5 fallback. */
function detach(element: Element | null | undefined): void {
  element?.parentNode?.removeChild(element);
}

export interface CompanyAccess {
  id: string;
  name: string;
  shortName: string | null;
  domain: string;
  role: string;
  type: 'primary' | 'external' | 'demo';
  industryType?: string | null;
  erpType?: string | null;
}

/** Short enough for the ~320 px pane, next to "Client" or "You". */
const ERP_SHORT: Record<string, string> = {
  TRIPLETEX: 'Tripletex',
  BUSINESS_CENTRAL: 'BC',
  DYNAMICS_365: 'D365',
};

export type SwitchHandler = (target: CompanyAccess) => Promise<void>;

interface MyCompaniesResponse {
  companies: CompanyAccess[];
}

export class CompanySwitcher {
  private mountId = 'company-switcher';
  private api = new ApiService();
  private companies: CompanyAccess[] = [];
  private currentCompanyId: string | null = null;
  private onSwitch: SwitchHandler;
  private isOpen = false;
  private switching = false;
  /**
   * The ERP the active company's formulas run against, shown on the chip. A
   * company can be on Tripletex in one environment and Business Central in
   * another, and the same numbers then look wrong rather than different.
   */
  private erpLabel: string | null = null;

  constructor(onSwitch: SwitchHandler) {
    this.onSwitch = onSwitch;
    document.addEventListener('click', this.handleOutsideClick);
  }

  async render(authHeaders: Record<string, string>, currentCompanyId: string | null): Promise<void> {
    this.currentCompanyId = currentCompanyId;
    try {
      const res = await this.api.get<MyCompaniesResponse>('/external-access/my-companies', {
        headers: authHeaders,
      });
      this.companies = res?.data?.companies ?? [];
    } catch {
      this.companies = [];
    }
    this.draw();
  }

  /**
   * Marks `companyId` as the one the add-in is on, without reading the list again. A switch made in
   * another workbook's pane changes the shared sign-in, and the chip must not keep naming the old one.
   */
  showCurrent(companyId: string | null): void {
    if (companyId === this.currentCompanyId) return;
    this.currentCompanyId = companyId;
    this.draw();
  }

  /** A company the user can switch to, as the last render listed it. */
  listed(companyId: string): CompanyAccess | undefined {
    return this.companies.find(c => c.id === companyId);
  }

  /**
   * Whether a workbook tied to `companyId` may belong to another company the user has: it is a client's
   * or a demo company, or the user has more than one company of their own. The demo companies every
   * user is offered are no such choice.
   */
  mayBelongElsewhere(companyId: string): boolean {
    const company = this.listed(companyId);
    if (company && company.type !== 'primary') return true;
    return this.companies.filter(c => c.type !== 'demo').length > 1;
  }

  setErp(label: string | null): void {
    if (label === this.erpLabel) return;
    this.erpLabel = label;
    this.draw();
  }

  destroy(): void {
    document.removeEventListener('click', this.handleOutsideClick);
    detach(document.getElementById(this.mountId));
  }

  /**
   * Clear the switcher's DOM and cached state without tearing down the instance.
   * Call this on logout so company names do not leak into the login screen.
   * The instance can be re-rendered after a fresh authentication.
   */
  clear(): void {
    this.companies = [];
    this.currentCompanyId = null;
    this.isOpen = false;
    this.switching = false;
    this.erpLabel = null;
    detach(document.getElementById(this.mountId));
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    const root = document.getElementById(this.mountId);
    if (!root) return;
    if (this.isOpen && !root.contains(e.target as Node)) {
      this.isOpen = false;
      this.draw();
    }
  };

  private draw(): void {
    let mount = document.getElementById(this.mountId);

    // Always render — single-company users get a static read-only context strip,
    // multi-company users get the full clickable picker. Both modes keep the
    // active company visible at all times so the user can never confuse which
    // company their formulas will run against.
    if (this.companies.length === 0) {
      detach(mount);
      return;
    }

    const isStatic = this.companies.length === 1;
    // A session always has an active company, so "Select company" is never the
    // truth — it only ever meant the stored token's companyId matched nothing
    // in the list (a token issued before the company was re-created, a demo
    // company that has since been deactivated). Falling back to the user's own
    // company is both accurate and what the rest of the pane is already
    // showing; getUserCompanies always includes it as type 'primary'.
    const current = this.companies.find(c => c.id === this.currentCompanyId)
      ?? this.companies.find(c => c.type === 'primary')
      ?? (isStatic ? this.companies[0] : undefined);

    if (!mount) {
      mount = document.createElement('div');
      mount.id = this.mountId;
      const insertAfter = document.getElementById('sandbox-banner') || document.querySelector('header.ms-welcome__header');
      if (insertAfter && insertAfter.parentElement) {
        insertAfter.parentElement.insertBefore(mount, insertAfter.nextSibling);
      } else {
        document.body.insertBefore(mount, document.body.firstChild);
      }
    }

    // Industry class drives the accent color (see main.css). Falls back to
    // neutral when industry is unknown or company has no industry tag.
    const industry = current?.industryType || 'unknown';
    mount.className = `company-switcher industry-${industry}${isStatic ? ' company-switcher--static' : ''}${current ? ` company-switcher--type-${current.type}` : ''}`;

    if (isStatic) {
      mount.innerHTML = `
        <div class="cs-static" aria-label="Active company">
          <span class="cs-label">Company</span>
          <span class="cs-current">
            ${this.escape(current?.name ?? '')}
            ${current ? this.badge(current) : ''}
            ${this.erpTag()}
          </span>
        </div>
      `;
      return;
    }

    const others = this.companies.filter(c => c.id !== this.currentCompanyId);

    mount.innerHTML = `
      <button type="button" class="company-switcher-trigger" id="cs-trigger" aria-haspopup="listbox" aria-expanded="${this.isOpen}">
        <span class="cs-label">Company</span>
        <span class="cs-current">
          ${this.escape(current?.name ?? 'Select company')}
          ${current ? this.badge(current) : ''}
          ${this.erpTag()}
        </span>
        <span class="cs-chevron">${chevronDownIcon}</span>
      </button>
      ${this.isOpen ? `
        <ul class="company-switcher-list" role="listbox">
          ${others.map(c => `
            <li role="option" data-company-id="${this.escape(c.id)}" class="cs-item industry-${c.industryType || 'unknown'} cs-item--type-${c.type}${this.switching ? ' cs-disabled' : ''}">
              <span class="cs-item-name">${this.escape(c.name)}</span>
              ${this.badge(c)}
            </li>
          `).join('')}
        </ul>
      ` : ''}
    `;

    const trigger = mount.querySelector<HTMLButtonElement>('#cs-trigger');
    trigger?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.switching) return;
      this.isOpen = !this.isOpen;
      this.draw();
    });

    mount.querySelectorAll<HTMLLIElement>('.cs-item').forEach(item => {
      item.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (this.switching) return;
        const id = item.dataset.companyId;
        const target = this.companies.find(c => c.id === id);
        if (!target) return;
        this.switching = true;
        this.isOpen = false;
        this.draw();
        try {
          await this.onSwitch(target);
        } finally {
          this.switching = false;
        }
      });
    });
  }

  private badge(c: CompanyAccess): string {
    const short = c.shortName ? `<span class="cs-shortname">${this.escape(c.shortName)}</span>` : '';
    if (c.type === 'demo') {
      const industryLabel = c.industryType
        ? `Demo · ${c.industryType.charAt(0).toUpperCase() + c.industryType.slice(1)}`
        : 'Demo';
      return `${short}<span class="cs-badge cs-badge-demo">${this.escape(industryLabel)}</span>`;
    }
    const erp = c.erpType ? ERP_SHORT[c.erpType] : undefined;
    const label = (who: string) => this.escape(erp ? `${who} · ${erp}` : who);
    if (c.type === 'external') return `${short}<span class="cs-badge cs-badge-external">${label('Client')}</span>`;
    return `${short}<span class="cs-badge cs-badge-primary">${label('You')}</span>`;
  }

  private erpTag(): string {
    return this.erpLabel ? `<span class="cs-erp" title="Formulas run against this ERP">${this.escape(this.erpLabel)}</span>` : '';
  }

  private escape(s: string): string {
    return s.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
  }
}
